import React, { PureComponent } from 'react'
import { Icon, Button, Tag, Input, Popconfirm, notification } from 'antd'
import { API_URL } from '../../request'
import styles from './index.less'

const VIDEO_EXTS = ['mp4', 'webm', 'ogg', 'mov', 'mkv', 'avi', 'm4v']

const STATUS_COLORS = {
  Completed: '#52c41a',
  Rejected: '#f5222d',
  Pending: '#fa8c16',
}

const isVideoUrl = (url) => {
  if (!url) return false
  const ext = url.split('?')[0].split('.').pop().toLowerCase()
  return VIDEO_EXTS.indexOf(ext) !== -1
}

const normalize = (items, typeHint) => {
  if (!items) return []
  const out = []
  for (let i = 0; i < items.length; i++) {
    const it = items[i]
    if (!it) continue
    // accept either a plain string url or an object with a url-ish field
    const rawUrl =
      typeof it === 'string'
        ? it
        : it.url || it.path || it.src || it.location || it.file
    if (!rawUrl) continue
    const url =
      rawUrl.indexOf('http') === 0
        ? rawUrl
        : API_URL + (rawUrl.charAt(0) === '/' ? '' : '/') + rawUrl
    const type =
      (typeof it === 'object' && it.type) ||
      typeHint ||
      (isVideoUrl(rawUrl) ? 'video' : 'image')
    const _id = typeof it === 'object' ? it._id || it.id : undefined
    const status =
      typeof it === 'object' ? it.status || it.reviewStatus : undefined
    out.push({ url: url, type: type, _id: _id, status: status, raw: it })
  }
  return out
}

const buildItems = (props) => {
  if (props.items) return normalize(props.items)
  return normalize(props.videos, 'video').concat(normalize(props.photos, 'image'))
}

class MediaViewer extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      // inline mode is always "open" at 0; lightboxOnly is driven by openIndex
      activeIndex: props.lightboxOnly
        ? (typeof props.openIndex === 'number' && props.openIndex >= 0 ? props.openIndex : null)
        : props.inline ? 0 : null,
      items: buildItems(props),
      // review state (shared by the review grid cards and the fullscreen bar)
      reviewLoadingIndex: null, // index currently being submitted
      rejectIndex: null, // index whose reject-reason input is open (rejectWithReason mode)
      rejectText: '',
      zoomed: false, // fullscreen image zoom toggle
      rotation: 0, // fullscreen image rotation in degrees (0/90/180/270)
    }
    this.touchStartX = null
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.photos !== this.props.photos ||
      prevProps.videos !== this.props.videos ||
      prevProps.items !== this.props.items
    ) {
      const items = buildItems(this.props)
      const oi = this.props.openIndex
      // when the list refreshes while the lightbox is open (e.g. after an
      // approve/reject), keep the user's CURRENT position instead of snapping
      // back to the originally-opened index. Only fall back to openIndex when
      // the viewer isn't already open.
      const cur = this.state.activeIndex
      const next = this.props.lightboxOnly
        ? (cur != null && cur < items.length
            ? cur
            : (typeof oi === 'number' && oi >= 0 && oi < items.length ? oi : null))
        : this.props.inline
          ? (items.length ? 0 : null)
          : (cur != null && cur < items.length ? cur : null)
      if (!this.props.lightboxOnly && !this.props.inline && cur != null && next === null) {
        // the rebuild closes the open fullscreen viewer (its index fell off the
        // end of the list) — release the key handler and body scroll lock
        this.detachKeys()
        this.restoreScroll()
      }
      this.setState({ items: items, activeIndex: next })
    }
    // controlled lightbox: open/close driven by the openIndex prop
    if (this.props.lightboxOnly && prevProps.openIndex !== this.props.openIndex) {
      const oi = this.props.openIndex
      if (typeof oi === 'number' && oi >= 0 && oi < this.state.items.length) {
        this.lockScroll()
        this.attachKeys()
        this.setState({ activeIndex: oi, zoomed: false, rotation: 0 })
      } else {
        this.detachKeys()
        this.restoreScroll()
        this.setState({ activeIndex: null, zoomed: false, rotation: 0 })
      }
    }
  }

  componentWillUnmount() {
    this.detachKeys()
    this.restoreScroll()
  }

  attachKeys = () => {
    document.addEventListener('keydown', this.onKey)
  }

  detachKeys = () => {
    document.removeEventListener('keydown', this.onKey)
  }

  lockScroll = () => {
    this.prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }

  restoreScroll = () => {
    if (this.prevOverflow !== undefined) {
      document.body.style.overflow = this.prevOverflow
      this.prevOverflow = undefined
    }
  }

  onKey = (e) => {
    if (this.state.activeIndex === null) return
    if (e.key === 'Escape' && !this.props.inline) this.close()
    else if (e.key === 'ArrowLeft') this.goPrev()
    else if (e.key === 'ArrowRight') this.goNext()
  }

  // fullscreen (grid) mode only
  open = (i) => {
    this.lockScroll()
    this.attachKeys()
    this.setState({ activeIndex: i, zoomed: false, rotation: 0 })
  }

  close = () => {
    this.detachKeys()
    this.restoreScroll()
    this.setState({ activeIndex: null, zoomed: false })
    if (this.props.lightboxOnly && this.props.onClose) this.props.onClose()
  }

  goTo = (i) =>
    this.setState({ activeIndex: i, rejectIndex: null, rejectText: '', zoomed: false, rotation: 0 })

  goPrev = () => {
    const total = this.state.items.length
    if (!total) return
    this.setState((s) => ({
      activeIndex: (s.activeIndex - 1 + total) % total,
      rejectIndex: null,
      rejectText: '',
      zoomed: false,
      rotation: 0,
    }))
  }

  goNext = () => {
    const total = this.state.items.length
    if (!total) return
    this.setState((s) => ({
      activeIndex: (s.activeIndex + 1) % total,
      rejectIndex: null,
      rejectText: '',
      zoomed: false,
      rotation: 0,
    }))
  }

  toggleZoom = (e) => {
    if (e) e.stopPropagation()
    this.setState((s) => ({ zoomed: !s.zoomed }))
  }

  // rotate the current image 90° clockwise (images only; videos are skipped)
  rotateCw = (e) => {
    if (e) e.stopPropagation()
    this.setState((s) => ({ rotation: (s.rotation + 90) % 360 }))
  }

  // Rotate control shown in the viewer top bar — only meaningful for images
  renderRotateButton = () => {
    const { items, activeIndex } = this.state
    const current = items[activeIndex]
    if (!current || current.type === 'video') return null
    return (
      <button className={styles.btn} title="Rotate 90°" onClick={this.rotateCw}>
        <Icon type="redo" />
      </button>
    )
  }

  startReject = (index, e) => {
    if (e) e.stopPropagation()
    this.setState({ rejectIndex: index, rejectText: '' })
  }

  cancelReject = (e) => {
    if (e) e.stopPropagation()
    this.setState({ rejectIndex: null, rejectText: '' })
  }

  // Per-item review for the item at `index`. With rejectWithReason, a Reject
  // requires a non-empty reason. Delegates to onReview, then updates status.
  doReview = async (index, status, e) => {
    if (e) e.stopPropagation()
    const { onReview, rejectWithReason } = this.props
    const { items, rejectText } = this.state
    const item = items[index]
    if (!onReview || !item) return
    const remarks =
      status === 'Rejected' && rejectWithReason ? (rejectText || '').trim() : ''
    if (status === 'Rejected' && rejectWithReason && !remarks) {
      notification.error({ message: 'Please enter a rejection reason' })
      return
    }
    this.setState({ reviewLoadingIndex: index })
    try {
      const result = await onReview(item, status, remarks)
      const newStatus = result || status
      const updated = items.slice()
      updated[index] = Object.assign({}, item, { status: newStatus })
      this.setState({
        items: updated,
        reviewLoadingIndex: null,
        rejectIndex: null,
        rejectText: '',
      })
    } catch (e2) {
      this.setState({ reviewLoadingIndex: null })
    }
  }

  stopProp = (e) => e.stopPropagation()

  onTouchStart = (e) => {
    this.touchStartX = e.touches && e.touches.length ? e.touches[0].clientX : null
  }

  onTouchEnd = (e) => {
    if (this.touchStartX === null) return
    const endX =
      e.changedTouches && e.changedTouches.length
        ? e.changedTouches[0].clientX
        : null
    if (endX === null) return
    const dx = endX - this.touchStartX
    if (Math.abs(dx) > 45) {
      if (dx < 0) this.goNext()
      else this.goPrev()
    }
    this.touchStartX = null
  }

  openInTab = (e) => {
    if (e) e.stopPropagation()
    const { items, activeIndex } = this.state
    if (activeIndex !== null && items[activeIndex]) {
      window.open(items[activeIndex].url, '_blank')
    }
  }

  renderThumb = (item, i) => (
    <div key={item.url + '-' + i} className={styles.thumb} onClick={() => this.open(i)}>
      {item.type === 'video' ? (
        <div className={styles.videoWrap}>
          <video src={item.url} muted preload="metadata" />
          <div className={styles.playOverlay}>
            <Icon type="play-circle" theme="filled" />
          </div>
        </div>
      ) : (
        <img src={item.url} alt="" loading="lazy" />
      )}
      <div className={styles.badge}>
        <Icon type={item.type === 'video' ? 'video-camera' : 'picture'} />
      </div>
    </div>
  )

  renderStrip = (item, i) => {
    const { activeIndex } = this.state
    const { reviewable } = this.props
    const cls = styles.stripItem + (i === activeIndex ? ' ' + styles.stripActive : '')
    const status = item.status || 'Pending'
    return (
      <div key={'strip-' + i} className={cls} onClick={() => this.goTo(i)}>
        {item.type === 'video' ? (
          <React.Fragment>
            <video src={item.url} muted preload="metadata" />
            <div className={styles.stripPlay}>
              <Icon type="play-circle" theme="filled" />
            </div>
          </React.Fragment>
        ) : (
          <img src={item.url} alt="" />
        )}
        {reviewable && (
          <span
            className={styles.statusDot}
            style={{ background: STATUS_COLORS[status] || STATUS_COLORS.Pending }}
            title={status}
          />
        )}
      </div>
    )
  }

  // Shared Complete / Reject controls for the item at `index`.
  renderReviewControls = (index) => {
    const { rejectWithReason, isLocked, statusLabels, completeLabel, hideControlsWhenReviewed } = this.props
    const { items, reviewLoadingIndex, rejectIndex, rejectText } = this.state
    const item = items[index]
    if (!item) return null
    const status = item.status || 'Pending'
    const displayStatus = (statusLabels && statusLabels[status]) || status
    const loading = reviewLoadingIndex === index
    const rejecting = rejectWithReason && rejectIndex === index
    const hardLocked = isLocked ? isLocked(item) : false
    // a rejected item can always be re-approved: show its tag + an Approve button
    if (!hardLocked && status === 'Rejected') {
      return (
        <div className={styles.reviewControls} onClick={this.stopProp}>
          <Tag color={STATUS_COLORS.Rejected} className={styles.reviewStatusTag}>
            {displayStatus}
          </Tag>
          <span className={styles.reviewActions}>
            <Button
              size="small"
              icon="check"
              loading={loading}
              className={styles.reviewComplete}
              onClick={(e) => this.doReview(index, 'Completed', e)}
            >
              {completeLabel || 'Complete'}
            </Button>
          </span>
        </div>
      )
    }
    // finalized (moved to stock) or — when opted in — an approved item: status only
    const completedLock = hideControlsWhenReviewed && status === 'Completed'
    if (hardLocked || completedLock) {
      return (
        <div className={styles.reviewControls} onClick={this.stopProp}>
          <Tag
            color={STATUS_COLORS[status] || STATUS_COLORS.Pending}
            className={styles.reviewStatusTag}
          >
            {displayStatus}
          </Tag>
        </div>
      )
    }
    return (
      <div className={styles.reviewControls} onClick={this.stopProp}>
        <Tag
          color={STATUS_COLORS[status] || STATUS_COLORS.Pending}
          className={styles.reviewStatusTag}
        >
          {displayStatus}
        </Tag>
        {!rejecting ? (
          <span className={styles.reviewActions}>
            <Button
              size="small"
              icon="check"
              loading={loading && status !== 'Completed'}
              className={styles.reviewComplete}
              onClick={(e) => this.doReview(index, 'Completed', e)}
            >
              {completeLabel || 'Complete'}
            </Button>
            <Button
              size="small"
              type="danger"
              icon="close"
              loading={loading && status !== 'Rejected'}
              onClick={(e) =>
                rejectWithReason
                  ? this.startReject(index, e)
                  : this.doReview(index, 'Rejected', e)
              }
            >
              Reject
            </Button>
          </span>
        ) : (
          <span className={styles.reviewRejectRow}>
            <Input
              size="small"
              autoFocus
              placeholder="Rejection reason..."
              value={rejectText}
              style={{ width: 180 }}
              onChange={(e) => this.setState({ rejectText: e.target.value })}
              onPressEnter={(e) => this.doReview(index, 'Rejected', e)}
              onClick={this.stopProp}
            />
            <Button
              size="small"
              type="danger"
              loading={loading}
              style={{ marginLeft: 6 }}
              onClick={(e) => this.doReview(index, 'Rejected', e)}
            >
              Confirm
            </Button>
            <Button
              size="small"
              style={{ marginLeft: 6 }}
              disabled={loading}
              onClick={this.cancelReject}
            >
              Cancel
            </Button>
          </span>
        )}
      </div>
    )
  }

  // Fullscreen-mode review bar (acts on the currently shown item)
  renderReviewBar = () => {
    if (!this.props.reviewable && !this.props.onDelete) return null
    const { items, activeIndex } = this.state
    const item = items[activeIndex]
    if (!item) return null
    return (
      <div className={styles.reviewBar} onClick={this.stopProp}>
        {this.props.reviewable && (
          <span className={styles.reviewLabel}>This {item.type}:</span>
        )}
        {this.props.reviewable && this.renderReviewControls(activeIndex)}
        {this.props.onDelete && (
          <Popconfirm
            title="Delete this media?"
            okText="Delete"
            cancelText="Cancel"
            onConfirm={() => this.props.onDelete(item)}
          >
            <Button size="small" type="danger" icon="delete">
              Delete
            </Button>
          </Popconfirm>
        )}
      </div>
    )
  }

  // One card per media item for the review grid
  renderReviewCard = (item, i) => {
    const status = item.status || 'Pending'
    const displayStatus = (this.props.statusLabels && this.props.statusLabels[status]) || status
    return (
      <div key={(item._id || item.url) + '-' + i} className={styles.reviewCard}>
        <div className={styles.reviewCardMedia} onClick={() => this.open(i)}>
          {item.type === 'video' ? (
            <React.Fragment>
              <video src={item.url} muted preload="metadata" />
              <div className={styles.playOverlay}>
                <Icon type="play-circle" theme="filled" />
              </div>
            </React.Fragment>
          ) : (
            <img src={item.url} alt="" loading="lazy" />
          )}
          <span
            className={styles.cardStatusRibbon}
            style={{ background: STATUS_COLORS[status] || STATUS_COLORS.Pending }}
          >
            {displayStatus}
          </span>
          <span className={styles.cardZoom}>
            <Icon type="search" /> Preview
          </span>
          {this.props.onDelete && (
            <span
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              style={{ position: 'absolute', top: 6, right: 6, zIndex: 2 }}
            >
              <Popconfirm
                title="Delete this media?"
                okText="Delete"
                cancelText="Cancel"
                onConfirm={() => this.props.onDelete(item)}
              >
                <button
                  type="button"
                  title="Delete"
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    border: 'none',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                  }}
                >
                  <Icon type="delete" />
                </button>
              </Popconfirm>
            </span>
          )}
        </div>
        {this.renderReviewControls(i)}
        {this.props.renderItemExtra && (
          <div className={styles.cardExtra}>
            {this.props.renderItemExtra(item, i)}
          </div>
        )}
      </div>
    )
  }

  // shared carousel body used by both inline and fullscreen modes
  renderViewerBody = () => {
    const { items, activeIndex } = this.state
    const total = items.length
    const current = items[activeIndex]
    if (!current) return null
    return (
      <React.Fragment>
        {total > 1 && (
          <button
            className={styles.nav + ' ' + styles.navPrev}
            title="Previous"
            onClick={(e) => { e.stopPropagation(); this.goPrev() }}
          >
            <Icon type="left" />
          </button>
        )}

        <div
          className={styles.stage + (this.state.zoomed ? ' ' + styles.stageZoom : '')}
          onClick={this.stopProp}
          onTouchStart={this.onTouchStart}
          onTouchEnd={this.onTouchEnd}
        >
          {current.type === 'video' ? (
            <video
              key={current.url}
              src={current.url}
              controls
              controlsList="nodownload"
              className={styles.player}
              style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000', outline: 'none' }}
            />
          ) : (
            <img
              className={styles.image + (this.state.zoomed ? ' ' + styles.imageZoomed : '')}
              src={current.url}
              alt=""
              title={this.state.zoomed ? 'Click to zoom out' : 'Click to zoom in'}
              style={{ transform: `rotate(${this.state.rotation}deg)` }}
              onClick={this.toggleZoom}
            />
          )}
        </div>

        {total > 1 && (
          <button
            className={styles.nav + ' ' + styles.navNext}
            title="Next"
            onClick={(e) => { e.stopPropagation(); this.goNext() }}
          >
            <Icon type="right" />
          </button>
        )}

        {(() => {
          const dp = this.props.detailsPanel
          const content = typeof dp === 'function' ? dp(current) : dp
          if (!content) return null
          return (
            <div
              onClick={this.stopProp}
              style={{
                position: 'absolute',
                left: 16,
                top: 70,
                width: 320,
                maxHeight: 'calc(100vh - 160px)',
                overflowY: 'auto',
                background: '#fff',
                borderRadius: 8,
                padding: 12,
                boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                zIndex: 4,
              }}
            >
              {content}
            </div>
          )
        })()}

        {this.renderReviewBar()}

        {total > 1 && (
          <div className={styles.filmstrip} onClick={this.stopProp}>
            {items.map(this.renderStrip)}
          </div>
        )}
      </React.Fragment>
    )
  }

  renderFullscreenModal = () => {
    const { items, activeIndex } = this.state
    const total = items.length
    const isOpen = activeIndex !== null && !!items[activeIndex]
    if (!isOpen) return null
    return (
      <div className={styles.modal} onClick={this.close}>
        <div className={styles.topbar} onClick={this.stopProp}>
          <span className={styles.counter}>
            {activeIndex + 1} / {total}
          </span>
          <div className={styles.actions}>
            <button className={styles.btn} title="Open in new tab" onClick={this.openInTab}>
              <Icon type="link" />
            </button>
            {this.renderRotateButton()}
            <button
              className={styles.btn + ' ' + styles.btnClose}
              title="Close (Esc)"
              onClick={(e) => { e.stopPropagation(); this.close() }}
            >
              <Icon type="close" />
            </button>
          </div>
        </div>
        {this.renderViewerBody()}
      </div>
    )
  }

  render() {
    const { inline, reviewable, lightboxOnly } = this.props
    const { items, activeIndex } = this.state
    const total = items.length

    // controlled lightbox: render only the fullscreen viewer (no grid)
    if (lightboxOnly) {
      return this.renderFullscreenModal()
    }

    if (!total) {
      return (
        <div className={styles.empty}>
          <Icon type="inbox" /> <span>No media available</span>
        </div>
      )
    }

    // Review grid: every photo/video is a card with its own Complete/Reject.
    // Clicking the media opens the fullscreen preview.
    if (reviewable && !inline) {
      const { groupBy, groupLabel } = this.props
      let grid
      if (typeof groupBy === 'function') {
        // group cards by key (e.g. Order ID) keeping each item's ORIGINAL index
        // (review/delete target the right item) and first-appearance order, so
        // every group renders on its own row(s) and the next group starts fresh.
        const groups = []
        const byKey = {}
        items.forEach((it, i) => {
          const key = groupBy(it)
          const k = key == null || key === '' ? '__none__' : String(key)
          if (!(k in byKey)) {
            byKey[k] = groups.length
            groups.push({ key, items: [] })
          }
          groups[byKey[k]].items.push({ it, i })
        })
        // show the "unsegregated" group (items with no group key) first, then the
        // rest in their first-appearance order (Array.sort is stable in V8)
        groups.sort((a, b) => {
          const an = a.key == null || a.key === ''
          const bn = b.key == null || b.key === ''
          return an === bn ? 0 : an ? -1 : 1
        })
        grid = groups.map((g, gi) => (
          <div key={'grp-' + gi} style={{ marginBottom: 10 }}>
            {typeof groupLabel === 'function' && groupLabel(g.key) ? (
              <div style={{ fontWeight: 600, fontSize: 13, color: '#555', margin: '4px 2px 6px' }}>
                {groupLabel(g.key)}
              </div>
            ) : null}
            <div className={styles.reviewGrid}>
              {g.items.map(({ it, i }) => this.renderReviewCard(it, i))}
            </div>
          </div>
        ))
      } else {
        grid = <div className={styles.reviewGrid}>{items.map(this.renderReviewCard)}</div>
      }
      return (
        <React.Fragment>
          {grid}
          {this.renderFullscreenModal()}
        </React.Fragment>
      )
    }

    // Inline carousel (optional embedded mode)
    if (inline) {
      return (
        <div className={styles.inlineViewer}>
          <div className={styles.inlineBar}>
            <span className={styles.counter}>
              {activeIndex + 1} / {total}
            </span>
            <span className={styles.actions}>
              <button className={styles.btn} title="Open in new tab" onClick={this.openInTab}>
                <Icon type="link" />
              </button>
              {this.renderRotateButton()}
            </span>
          </div>
          {this.renderViewerBody()}
        </div>
      )
    }

    // Default: thumbnail grid + fullscreen modal on click
    const isOpen = activeIndex !== null
    return (
      <React.Fragment>
        <div className={styles.grid}>{items.map(this.renderThumb)}</div>

        {isOpen && (
          <div className={styles.modal} onClick={this.close}>
            <div className={styles.topbar} onClick={this.stopProp}>
              <span className={styles.counter}>
                {activeIndex + 1} / {total}
              </span>
              <div className={styles.actions}>
                <button className={styles.btn} title="Open in new tab" onClick={this.openInTab}>
                  <Icon type="link" />
                </button>
                {this.renderRotateButton()}
                <button
                  className={styles.btn + ' ' + styles.btnClose}
                  title="Close (Esc)"
                  onClick={(e) => { e.stopPropagation(); this.close() }}
                >
                  <Icon type="close" />
                </button>
              </div>
            </div>
            {this.renderViewerBody()}
          </div>
        )}
      </React.Fragment>
    )
  }
}

export default MediaViewer
